import { DEFAULT_LOCALE, isLocale, type Locale } from "./locales.ts";

/**
 * One answer to "what language is this person reading in", used by all four surfaces.
 *
 * Before P12 the API, the staff app, the booking island and the mail payload builder each
 * had their own version of this, and they disagreed about `nb` — which is how a Norwegian
 * customer gets a Norwegian page and a Swedish confirmation email.
 *
 * The order is the caller's, not this function's. It takes candidates and returns the first
 * one it has a catalog for, because the priority genuinely differs: for a signed-in user the
 * account setting wins, for a customer mid-booking the franchise's country is the better
 * guess than the browser of whoever set the laptop up. What must not differ is how a tag is
 * read, and that is what lives here.
 */

/** `sv-SE` → `sv`. `nb`/`nn` are both `no` to us: one Norwegian catalog, Bokmål. */
export function normaliseTag(tag: string | null | undefined): Locale | null {
  if (tag === null || tag === undefined) return null;
  const base = tag.split(/[-_]/)[0]?.trim().toLowerCase();
  if (base === undefined || base.length === 0) return null;
  const candidate = base === "nb" || base === "nn" ? "no" : base;
  return isLocale(candidate) ? candidate : null;
}

/**
 * The first candidate we can actually render, else `sv`.
 *
 * `DEFAULT_LOCALE` is the floor rather than English (BUILD.md §1.2): 23 of the 24 locations
 * are Swedish, and a fallback to English would be a fallback to the language fewest of the
 * people using this speak.
 */
export function resolveLocale(candidates: readonly (string | null | undefined)[]): Locale {
  for (const candidate of candidates) {
    const locale = normaliseTag(candidate);
    if (locale !== null) return locale;
  }
  return DEFAULT_LOCALE;
}

/**
 * An `Accept-Language` header, in the order the browser sent it.
 *
 * Quality values are ignored: the header is already ordered, and the first entry we have a
 * catalog for is a better answer than the arithmetic.
 */
export function localesFromAcceptLanguage(header: string | null | undefined): readonly string[] {
  if (header === null || header === undefined) return [];
  return header.split(",").map((entry) => entry.split(";")[0]?.trim() ?? "");
}

/**
 * A country's own language, for the step of the booking flow where a franchise is known and
 * the customer is not.
 *
 * `countries.default_locale` in the database is the source of record and outranks this —
 * this is for the places that hold a country code and nothing else. DK and FI are here
 * because BUILD.md plans them; both currently fall through `t` to English.
 */
const COUNTRY_LOCALE: Readonly<Record<string, Locale>> = {
  SE: "sv",
  NO: "no",
  DK: "da",
  FI: "fi",
};

export function localeForCountry(countryCode: string | null | undefined): Locale | null {
  if (countryCode === null || countryCode === undefined) return null;
  return COUNTRY_LOCALE[countryCode.toUpperCase()] ?? null;
}

/** BCP 47 tags, so `Intl` picks the right conventions rather than guessing a region. */
const BCP47: Readonly<Record<Locale, string>> = {
  sv: "sv-SE",
  no: "nb-NO",
  // `en-GB`, not `en-US`: a European product whose third language this is.
  en: "en-GB",
  da: "da-DK",
  fi: "fi-FI",
};

export function bcp47(locale: Locale): string {
  return BCP47[locale];
}
